/**
 * Mock mode: the contracts' three-agent collision, run live in the page against the in-memory mock coordinator.
 *
 * The scenario runs step by step on a timer (the mock's clock moves a minute per step, so timestamps match the
 * recording). Its two approvals are gated: the run stops at each one until someone presses the matching button,
 * which calls the mock's `approvePlan` or `approvePromotion` itself.
 */
import type { Coordinator, PlatformEvent } from "@botallion/contracts";
import { runThreeAgentCollision, SCENARIO_START } from "@botallion/contracts/fixtures";
import { createManualClock, createMockCoordinator } from "@botallion/contracts/mock";
import { gateApprovals, type GateNotice } from "./gate.ts";

export interface MockRunOptions {
  onEvent: (event: PlatformEvent) => void;
  /** Real milliseconds between scenario steps, read before every step (so speed can change mid-run). */
  stepMs: () => number;
  onWaiting?: (waiting: GateNotice[]) => void;
  onDone?: (error: Error | null) => void;
  sleep?: (ms: number) => Promise<void>;
}

export interface MockRun {
  /** The mock itself. Approvals go here directly. */
  coordinator: Coordinator;
  pause(): void;
  resume(): void;
  readonly paused: boolean;
  stop(): void;
  done: Promise<void>;
}

class Stopped extends Error {}

export function startMockRun(options: MockRunOptions): MockRun {
  const clock = createManualClock(SCENARIO_START);
  const mock = createMockCoordinator({ now: clock.now });
  const sub = mock.subscribe({ since: 0 }, options.onEvent);
  const gated = gateApprovals(mock, options.onWaiting);
  const sleep = options.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  let stopped = false;
  let paused = false;
  let wake: (() => void) | null = null;

  const tick = async () => {
    await sleep(options.stepMs());
    while (paused && !stopped) await new Promise<void>((r) => (wake = r));
    if (stopped) throw new Stopped();
    clock.advance(60_000);
  };

  const done = runThreeAgentCollision(gated, { tick }).then(
    () => {
      sub.close();
      options.onDone?.(null);
    },
    (e: unknown) => {
      sub.close();
      options.onDone?.(e instanceof Stopped || stopped ? null : (e as Error));
    },
  );

  return {
    coordinator: mock,
    get paused() {
      return paused;
    },
    pause() {
      paused = true;
    },
    resume() {
      paused = false;
      wake?.();
      wake = null;
    },
    stop() {
      stopped = true;
      paused = false;
      wake?.();
      gated.release();
      sub.close();
    },
    done,
  };
}
